import { ComponentType } from 'react'

import { useQuery } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'

import {
  PaymentInfoOrdersDocument,
  PaymentInfoOrdersQuery,
  PaymentInfoOrdersQueryVariables,
  PaymentStatus,
  RequestStatus,
} from '__generated__/atlas-types'

import { atlasClient } from 'graphql/client'

interface PaymentInfoNode {
  id: string
  paymentInfo?:
    | {
        paymentStatus: PaymentStatus
        requestStatus: RequestStatus
        remainingBalance?: string
      }
    | null
    | undefined
}

const updateOrders = (
  paymentInfoOrders: { node: PaymentInfoNode }[],
  orders: Record<string, string>[],
) => {
  if (!paymentInfoOrders.length) {
    return orders
  }

  return orders.map((order, index) => {
    let matchingOrder: PaymentInfoNode | undefined =
      paymentInfoOrders[index]?.node

    if (matchingOrder?.id !== order.id) {
      matchingOrder = paymentInfoOrders.find(
        (orderNode) => orderNode.node.id === order.id,
      )?.node
    }

    if (!matchingOrder || !matchingOrder.paymentInfo) {
      return { ...order }
    }

    return {
      ...order,
      paymentStatus: matchingOrder.paymentInfo.paymentStatus,
      requestStatus: matchingOrder.paymentInfo.requestStatus,
      remainingBalance: matchingOrder.paymentInfo.remainingBalance ?? '',
    }
  })
}

const withOrderPaymentInfo =
  (
    WrappedComponent: ComponentType<{
      orders?: Record<string, string>[]
      isPaymentInfoLoading?: boolean
      hasPaymentInfoErrors?: boolean
    }>,
  ) =>
  (props: {
    orders: Record<string, string>[]
    flags: { joorPayUseReach: boolean }
    permissions: { allowInvoiceRetailers: boolean }
  }) => {
    const {
      orders,
      flags: { joorPayUseReach },
      permissions: { allowInvoiceRetailers },
    } = props
    const { data, loading, error } = useQuery<
      PaymentInfoOrdersQuery,
      PaymentInfoOrdersQueryVariables
    >(PaymentInfoOrdersDocument, {
      variables: {
        filters: {
          ids: Array.isArray(orders) ? orders.map((order) => order.id) : [],
        },
      },
      client: atlasClient,
      skip: isEmpty(orders) || !joorPayUseReach || !allowInvoiceRetailers,
      fetchPolicy: 'cache-first',
    })

    const paymentInfoOrders = data?.orders.edges || []
    const updatedOrders = updateOrders(paymentInfoOrders, orders)

    return (
      <WrappedComponent
        {...props}
        orders={updatedOrders}
        isPaymentInfoLoading={loading}
        hasPaymentInfoErrors={!!error}
      />
    )
  }

export default withOrderPaymentInfo
